import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  output,
  signal,
  untracked,
} from '@angular/core';
import { take } from 'rxjs';
import { A11yModule } from '@angular/cdk/a11y';

import { LdgImgComponent } from '../../../../atoms/ldg-img/ldg-img.component';
import { ActionTypesEnum } from '../../../../common/app-config/filter-models/misc';
import { FormatService } from '../../../../common/format/format.service';
import { SocialChannelEnum } from '../../../../common/general-types/briefs.dto';
import { PaymentMethodTypeEnum } from '../../../../common/general-types/shout-plus.dto';
import { ViewportOverlayDirective } from '../../../../common/directives/viewport-overlay/viewport-overlay.directive';
import { CentralManagementService } from '../../../../services/central-management/central-management.service';
import { ImageService } from '../../../../services/image/image.service';
import { PricingModelV2Service } from '../../../../services/pricing-model-v2/pricing-model-v2.service';
import {
  ChannelFollowerOverlay,
  resolveChannelFollowers,
} from '../../../../services/pricing-model-v2/channel-followers.util';
import { UltraSocialService } from '../../../../services/ultra-social/ultra-social';
import { WalletService } from '../../../../services/wallet/wallet.service';
import {
  getResolvedPaymentMethodType,
  isShoutPlusBrief,
} from '../../../../shout-plus/utils/shout-plus-hire.util';
import { ShoutPlusBriefService } from '../../../../shout-plus/services/shout-plus-brief/shout-plus-brief.service';
import { MagicSearchService } from '../../../creators/service/magic-search/magic-search.service';
import { ManageBriefTabsService } from '../../../manage-brief-tabs/service/manage-brief-tabs/manage-brief-tabs.service';
import {
  placementChannelsFromIds,
  socialChannelIdsOnPieces,
} from '@/app/common/brief-pieces/piece-rules';
import {
  channelAudienceBrand,
  NOTHING_OWED_COPY,
  pieceHireAction,
  toMajor,
} from '@/app/common/brief-pieces/piece-hire-quote';
import { PieceHireQuoteStore } from '@/app/services/brief-application/piece-hire-quote.store';
import { personInitials } from '@/app/common/format/person-initials';
import {
  channelBrandColour,
  channelMark,
  readChannels,
  togglableChannels,
} from '../../../manage-brief-tabs/tabs/assignment/cross-platform-state.util';
import {
  HireCostBreakdown,
  hireCostBreakdown,
  crossPostNetTotal,
} from '../../../manage-brief-tabs/tabs/assignment/hire-cost.util';
import { getSocialChannelLabel } from '../../../insights/tabs/creator/molecules/application-drawer/molecules/application-actions/application-actions.helpers';
import {
  followerMetric,
  headerDescription,
  networkCountLabel,
  notPutForwardEntries,
  notPutForwardSentence,
  showsNoBonusNote,
  totalSubLine,
} from './review-application-modal.helpers';
import {
  breakdownFigureClass,
  breakdownLabelClass,
  breakdownRowClass,
  checkboxClass,
  rowClass,
  walletAfterClass,
} from './review-application-modal.styles';

/** One network row in the body, and its matching line in the cost breakdown. */
interface NetworkRow {
  socialChannel: number;
  label: string;
  mark: string;
  brandColour: string;
  /** Net rate for this network, excluding VAT. Null when we cannot price it. */
  rate: number | null;
  /** Null when no source knows, which is not the same as an audience of zero. */
  followers: number | null;
  included: boolean;
  /** The primary network is part of the hire by definition and cannot be switched off. */
  required: boolean;
}

/** How long a channel toggle waits before re-quoting, so quick clicks cost one request. */
const REQUOTE_DEBOUNCE_MS = 250;

/**
 * The brand's one decision at hire: which of the networks the creator put forward to
 * include, with the cost moving as they decide.
 *
 * Only opened for briefs that offer cross-post networks. Single-channel and UGC briefs
 * are hired from the Assignment tab's creator side panel (`HireBreakdown`), where there is
 * no decision to make and the itemised price list is the more useful thing to show.
 *
 * Reads its subject from `CentralManagementService.itemisedHireDataSginal`, which every
 * route into hiring sets.
 *
 * Inclusion lives in `CentralManagementService.crossPlatformHireDraft`, shared with the
 * Assignment table's tiles: one draft, two surfaces. Nothing is committed until Hire.
 */
@Component({
  selector: 'mb-review-application',
  imports: [CurrencyPipe, LdgImgComponent, A11yModule],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './review-application-modal.html',
  styleUrl: './review-application-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'cancel()',
  },
})
export class ReviewApplicationModal {
  readonly close = output<void>();

  private readonly centralManagement = inject(CentralManagementService);
  private readonly pricingModelService = inject(PricingModelV2Service);
  private readonly shoutPlusBriefService = inject(ShoutPlusBriefService);
  private readonly magicSearchService = inject(MagicSearchService);
  private readonly manageBriefTabsService = inject(ManageBriefTabsService);
  private readonly walletService = inject(WalletService);
  private readonly ultraSocialService = inject(UltraSocialService);
  private readonly pieceQuotes = inject(PieceHireQuoteStore);
  protected readonly imageService = inject(ImageService);
  protected readonly formatService = inject(FormatService);

  /**
   * Inclusion as it stood when this application's modal first opened.
   *
   * Cancel abandons the hire, not every edit ever made: the brand can also exclude a
   * network from the Assignment table's tiles before opening the modal, and those
   * belong to the row rather than to this dialog. Restoring the snapshot undoes only
   * what happened in here. Keyed by application id so re-opening the same row twice in
   * one session does not resurrect a stale selection.
   */
  private readonly snapshots = new Map<string, number[]>();

  /**
   * Follower counts the API resolved for this creator, keyed by the creator they belong
   * to so a re-open for somebody else can never inherit them.
   *
   * Only fetched when the creator record itself cannot answer — see the effect below.
   */
  private readonly followerOverlay = signal<{
    uid: string;
    followers: ChannelFollowerOverlay;
  } | null>(null);

  protected readonly followersLoading = signal(false);

  /** Creators we have already asked about, so a re-render cannot re-request. */
  private readonly followersRequested = new Set<string>();

  private readonly data = computed(() => this.centralManagement.itemisedHireDataSginal());

  protected readonly brief = computed(() => this.data()?.brief);
  protected readonly creator = computed(() => this.data()?.creator);
  protected readonly application = computed(() => this.data()?.briefApplication);

  protected readonly currencyCode = computed(
    () => this.brief()?.currentPrice?.currencyCode?.toUpperCase() ?? 'USD',
  );

  private readonly paymentMethod = computed(() =>
    this.shoutPlusBriefService.briefPaymentMethodSignal(),
  );

  private readonly resolvedPaymentType = computed(() =>
    getResolvedPaymentMethodType(this.brief(), this.paymentMethod()),
  );

  /** SHOUT+ briefs paid in product or vouchers carry no audience bonus, so no cross-post rate. */
  private readonly audienceBonusExcluded = computed(
    () =>
      isShoutPlusBrief(this.brief()) &&
      this.resolvedPaymentType() !== PaymentMethodTypeEnum.CASH,
  );

  protected readonly primaryChannel = computed<number | null>(() => {
    const channel = this.brief()?.shoutBrief?.socialPlatform;
    return channel == null ? null : Number(channel);
  });

  /** A stored piece list is hired here by accepting the channels the creator offered. */
  protected readonly pieceHire = computed(() => (this.brief()?.pieces?.length ?? 0) > 0);

  protected readonly NOTHING_OWED_COPY = NOTHING_OWED_COPY;

  /** Whether the first quote for this application has gone out, so later ones can wait. */
  private readonly quotedApplications = new Set<string>();

  constructor() {
    // Snapshot on first sight of an application, before the brand can touch anything.
    effect(() => {
      const applicationId = this.application()?.id;
      if (!applicationId || this.snapshots.has(applicationId)) {
        return;
      }
      const seeded = untracked(() =>
        this.centralManagement.crossPlatformDraftFor(applicationId, this.putForwardChannels()),
      );
      this.snapshots.set(applicationId, [...seeded]);
    });

    // SHOUT+ payment method decides both the primary fee and whether VAT applies.
    effect(() => {
      const brief = this.brief();
      untracked(() => this.shoutPlusBriefService.syncBriefPaymentMethod(brief));
    });

    /**
     * Piece-list hires are priced by the API. Quote the current choice (debounced after
     * the first), and every offered channel once, so a channel switched off still shows
     * what it would cost.
     */
    effect(() => {
      const applicationId = this.application()?.id;
      if (!this.pieceHire() || !applicationId) {
        return;
      }
      const included = this.includedChannels();
      const offered = this.putForwardChannels();
      untracked(() => {
        const first = !this.quotedApplications.has(applicationId);
        this.quotedApplications.add(applicationId);
        if (first) {
          this.pieceQuotes.request(applicationId, offered);
        }
        this.pieceQuotes.request(applicationId, included, first ? 0 : REQUOTE_DEBOUNCE_MS);
      });
    });

    // The footer quotes the wallet, which the page may not have loaded yet.
    effect(() => {
      const businessAccountId = this.brief()?.businessAccountId;
      if (!businessAccountId) {
        return;
      }
      untracked(() => {
        if (this.walletService.balanceBusinessAccountId() === businessAccountId) {
          return;
        }
        this.walletService.getBalance(businessAccountId).subscribe({
          error: (error: unknown) => console.error('Could not load wallet balance', error),
        });
      });
    });

    /**
     * Fill in follower counts the creator record cannot supply.
     *
     * Only asked when a network we are about to put a price against has no count on the
     * creator at all — the endpoint goes out to Ultra Social and writes back what it
     * learns, so it is worth a round trip, but not on every open.
     */
    effect(() => {
      const creator = this.creator();
      const uid = creator?.blazeFirebaseUid;
      const channels = [this.primaryChannel(), ...this.putForwardChannels()];

      if (!creator || !uid) {
        return;
      }

      untracked(() => {
        if (this.followersRequested.has(uid)) {
          return;
        }

        const anyUnknown = channels.some(
          (channel) => channel !== null && resolveChannelFollowers(creator, channel) === null,
        );
        if (!anyUnknown) {
          return;
        }

        this.followersRequested.add(uid);
        this.followersLoading.set(true);

        this.ultraSocialService
          .getCreatorFollowers(uid)
          .pipe(take(1))
          .subscribe({
            next: (response) => {
              this.followerOverlay.set({ uid, followers: response?.followers ?? {} });
              this.followersLoading.set(false);
            },
            error: (error: unknown) => {
              console.error('Could not load follower counts', error);
              this.followersLoading.set(false);
            },
          });
      });
    });
  }

  /** The overlay, but only when it belongs to the creator currently on screen. */
  private readonly activeOverlay = computed<ChannelFollowerOverlay | null>(() => {
    const overlay = this.followerOverlay();
    const uid = this.creator()?.blazeFirebaseUid;
    return overlay && uid && overlay.uid === uid ? overlay.followers : null;
  });

  /* ── The decision ───────────────────────────────────────────────────────── */

  /**
   * Networks this creator offered, in brief order.
   *
   * The brand chooses from this list and never from the brief's full list — adding a
   * network the creator did not volunteer is a post-live cross-post request, which is
   * a different flow with different consent.
   */
  protected readonly putForwardChannels = computed(() => {
    const application = this.application();
    if (!application) {
      return [];
    }
    const pieces = this.brief()?.pieces;
    if (pieces?.length) {
      const offered = new Set(readChannels(application.crossPlatformsPutForward));
      return socialChannelIdsOnPieces(pieces).filter((channelId) => offered.has(channelId));
    }
    return togglableChannels(application, this.brief()?.shoutBrief);
  });

  private readonly includedChannels = computed(() => {
    const applicationId = this.application()?.id;
    if (!applicationId) {
      return [] as number[];
    }
    const offered = this.putForwardChannels();
    const draft = this.centralManagement.crossPlatformDraftFor(applicationId, offered);
    return offered.filter((channel) => draft.includes(channel));
  });

  protected readonly notPutForward = computed(() => {
    if (!this.pieceHire()) {
      return notPutForwardEntries(this.application(), this.brief()?.shoutBrief);
    }
    const offered = new Set(readChannels(this.application()?.crossPlatformsPutForward));
    return socialChannelIdsOnPieces(this.brief()?.pieces ?? [])
      .filter((channelId) => !offered.has(channelId))
      .map((socialChannel) => ({
        socialChannel,
        label: getSocialChannelLabel(socialChannel),
        reason: 'not-offered' as const,
      }));
  });

  protected readonly notPutForwardText = computed(() =>
    notPutForwardSentence(this.notPutForward()),
  );

  /** Whether any missing network is missing because there is no account behind it. */
  protected readonly notPutForwardHasNoAccount = computed(() =>
    this.notPutForward().some((entry) => entry.reason === 'no-account'),
  );

  protected toggle(socialChannel: number): void {
    const applicationId = this.application()?.id;
    if (!applicationId) {
      return;
    }
    this.centralManagement.toggleCrossPlatformDraft(
      applicationId,
      Number(socialChannel),
      this.putForwardChannels(),
    );
  }

  /* ── Rows ───────────────────────────────────────────────────────────────── */

  /**
   * The required primary network.
   *
   * Its rate is the whole primary fee — base, rank and audience bonus across every
   * video — because that is what the brand commits for the post, and the breakdown has
   * to sum to the total.
   */
  protected readonly primaryRow = computed<NetworkRow | null>(() => {
    const channel = this.primaryChannel();
    if (channel == null) {
      return null;
    }
    return {
      socialChannel: channel,
      label: getSocialChannelLabel(channel),
      mark: channelMark(channel),
      brandColour: channelBrandColour(channel),
      rate: this.primaryNet(),
      followers: this.followersFor(channel),
      included: true,
      required: true,
    };
  });

  protected readonly putForwardRows = computed<NetworkRow[]>(() => {
    const included = new Set(this.includedChannels());
    return this.putForwardChannels().map((socialChannel) => ({
      socialChannel,
      label: getSocialChannelLabel(socialChannel),
      mark: channelMark(socialChannel),
      brandColour: channelBrandColour(socialChannel),
      rate: this.crossRate(socialChannel),
      followers: this.followersFor(socialChannel),
      included: included.has(socialChannel),
      required: false,
    }));
  });

  private followersFor(socialChannel: number): number | null {
    const creator = this.creator();
    if (!creator) {
      return null;
    }
    return this.pricingModelService.getFollowersForChannel(
      socialChannel as SocialChannelEnum,
      creator,
      this.activeOverlay(),
    );
  }

  /**
   * Rate for one cross-post, or null when we cannot honestly name one.
   *
   * The rate is a band lookup against the creator's audience, so without a follower
   * count there is no rate — only a £0.00 that reads like a free cross-post and is not.
   * A brief that excludes the audience bonus outright is a different thing: that really
   * is zero, and says so.
   */
  private crossRate(socialChannel: number): number | null {
    if (this.pieceHire()) {
      return this.pieceChannelRate(socialChannel);
    }
    const brief = this.brief();
    const creator = this.creator();
    if (!brief || !creator) {
      return null;
    }
    if (this.audienceBonusExcluded()) {
      return 0;
    }
    if (this.followersFor(socialChannel) === null) {
      return null;
    }
    return this.pricingModelService.getAudiencePrice(
      socialChannel as SocialChannelEnum,
      brief,
      creator,
      this.activeOverlay(),
    );
  }

  /**
   * Whether the primary post's audience bonus is unknown rather than genuinely nil.
   *
   * `primaryNet` still holds the base-and-rank fee, which is knowable without a
   * follower count — it is only the audience component that is missing.
   */
  protected readonly primaryBonusPending = computed(() => {
    const channel = this.primaryChannel();
    if (channel === null || this.audienceBonusExcluded()) {
      return false;
    }
    if (isShoutPlusBrief(this.brief()) && this.resolvedPaymentType() === PaymentMethodTypeEnum.CASH) {
      return false;
    }
    return this.followersFor(channel) === null;
  });

  /** Any figure on screen that is a floor rather than the real number. Never for a piece hire: the API prices it. */
  protected readonly hasPendingPricing = computed(
    () =>
      !this.pieceHire() &&
      (this.primaryBonusPending() ||
        this.includedChannels().some((channel) => this.crossRate(channel) === null)),
  );

  /* ── Money ──────────────────────────────────────────────────────────────── */

  /** The server quote for exactly the channels now included, or null while it loads. */
  private readonly currentQuote = computed(() => {
    const applicationId = this.application()?.id;
    return this.pieceHire() && applicationId
      ? this.pieceQuotes.quoteFor(applicationId, this.includedChannels())
      : null;
  });

  /** The quote on screen: the current one, else the last one while a re-quote loads. */
  protected readonly pieceQuote = computed(() => {
    const applicationId = this.application()?.id;
    if (!this.pieceHire() || !applicationId) {
      return null;
    }
    return (this.currentQuote() ?? this.pieceQuotes.lastQuoteFor(applicationId))?.quote ?? null;
  });

  /** True while the figures on screen are not yet for the current choice. */
  protected readonly pieceQuoteStale = computed(() => this.pieceHire() && !this.currentQuote());

  protected readonly pieceQuoteError = computed(() => {
    const applicationId = this.application()?.id;
    if (!this.pieceHire() || !applicationId) {
      return null;
    }
    return this.pieceQuotes.entryFor(applicationId, this.includedChannels())?.error ?? null;
  });

  /** The hire came back with a new price; hiring again confirms it. */
  protected readonly piecePriceChanged = computed(() => {
    const applicationId = this.application()?.id;
    return this.pieceHire() && !!applicationId && this.pieceQuotes.priceChanged(applicationId);
  });

  /** `networks`, `delivery-only` or `nothing-owed`, from the current quote; null while it loads. */
  protected readonly pieceHireAction = computed(() => {
    const quote = this.currentQuote()?.quote;
    return quote ? pieceHireAction(quote) : null;
  });

  /** A piece hire only goes ahead on a current quote that owes something (F2). */
  protected readonly hireBlocked = computed(
    () => this.pieceHire() && (this.pieceHireAction() === null || this.pieceHireAction() === 'nothing-owed'),
  );

  /**
   * Audience fees for one offered channel, from the API.
   *
   * An included channel reads the quote on screen; a switched-off one reads the quote
   * for every offered channel, so the brand can see what ticking it back would add.
   */
  private pieceChannelRate(socialChannel: number): number | null {
    const channel = placementChannelsFromIds([socialChannel])[0];
    const applicationId = this.application()?.id;
    if (!channel || !applicationId) {
      return null;
    }
    const quote = this.includedChannels().includes(socialChannel)
      ? this.pieceQuote()
      : this.pieceQuotes.quoteFor(applicationId, this.putForwardChannels())?.quote ?? null;
    return quote ? toMajor(channelAudienceBrand(quote, channel)) : null;
  }

  /** Primary fee, excluding VAT. A SHOUT+ cash brief pays a fixed amount instead. */
  private readonly primaryNet = computed(() => {
    const brief = this.brief();
    if (!brief) {
      return 0;
    }
    if (isShoutPlusBrief(brief) && this.resolvedPaymentType() === PaymentMethodTypeEnum.CASH) {
      return Number(this.paymentMethod()?.value ?? 0) / 100;
    }
    const creator = this.creator();
    return creator
      ? this.pricingModelService.getTotalPrice(brief, creator, this.activeOverlay())
      : 0;
  });

  /**
   * Unknown cross-post rates are left out of the total rather than summed as zero.
   *
   * `crossPostNetTotal` coerces anything non-finite to 0, so passing nulls through would
   * quietly re-create the bug this is fixing. Excluding them makes the total a floor,
   * which the template labels as such.
   */
  protected readonly breakdown = computed<HireCostBreakdown>(() => {
    if (this.pieceHire()) {
      // The API's figures as they are: VAT and the total are never recomputed here.
      const quote = this.pieceQuote();
      return {
        primaryNet: toMajor(quote?.production.brand),
        crossNet: toMajor(quote?.audienceTotal.brand),
        net: toMajor(quote?.brandNet),
        vat: toMajor(quote?.vat),
        total: toMajor(quote?.brandTotal),
        primaryGross: 0,
        crossGross: 0,
      };
    }
    return hireCostBreakdown({
      primaryNet: this.primaryNet(),
      crossNet: crossPostNetTotal(
        this.includedChannels()
          .map((channel) => this.crossRate(channel))
          .filter((rate): rate is number => rate !== null),
      ),
      brief: this.brief(),
      paymentMethod: this.paymentMethod(),
    });
  });

  /** Networks in this hire. A piece list has no required primary channel. */
  protected readonly networkCount = computed(() =>
    this.pieceHire() ? this.includedChannels().length : this.includedChannels().length + 1,
  );

  protected readonly networkCountText = computed(() => networkCountLabel(this.networkCount()));

  /** `3 networks` / `1 network`, for the header sentence and the total sub-line. */
  protected networkCountLabelFor(count: number): string {
    return networkCountLabel(count);
  }

  /**
   * The line under a network's name.
   *
   * Followers on that channel, and nothing invented: there is no reach estimate behind
   * the design's "est. reach / cost per 1K", so quoting one would be a guess dressed as
   * a figure. A channel the creator has no audience on still shows its row — an unpriced
   * cross-post is a decision, not an absence.
   */
  protected metricLine(row: NetworkRow): string {
    const parts: string[] = [];
    const metric = followerMetric(row.followers, this.followersLoading());

    switch (metric.kind) {
      case 'known':
        parts.push(`${this.formatService.convertIntToString(metric.followers)} followers`);
        break;
      case 'loading':
        parts.push('Checking follower data…');
        break;
      case 'unavailable':
        parts.push('Follower data unavailable');
        break;
    }

    if (showsNoBonusNote(row)) {
      parts.push('no audience bonus at this tier');
    }

    return parts.join(' · ');
  }

  protected readonly subLine = computed(() => totalSubLine(this.includedChannels().length));

  protected readonly includedCrossCount = computed(() => this.includedChannels().length);

  /**
   * What the wallet holds once this hire is committed, or null when we cannot say.
   *
   * Checked against `balanceBusinessAccountId` rather than trusting the signal: the
   * wallet signals are global and `getBalance` only writes them for the currently
   * focussed brand, so a brief belonging to another brand would otherwise be captioned
   * with somebody else's money.
   */
  protected readonly walletAfter = computed(() => {
    const businessAccountId = this.brief()?.businessAccountId;
    const available = this.walletService.availableBalance();

    if (!businessAccountId || available == null) {
      return null;
    }
    if (this.walletService.balanceBusinessAccountId() !== businessAccountId) {
      return null;
    }
    return available - this.breakdown().total;
  });

  /** The wallet is held in its own currency, which need not be the brief's. */
  protected readonly walletCurrencyCode = computed(
    () => this.walletService.currencyCode()?.toUpperCase() || this.currencyCode(),
  );

  protected readonly walletFigureClass = computed(() =>
    walletAfterClass((this.walletAfter() ?? 0) < 0),
  );

  /* ── Header copy ────────────────────────────────────────────────────────── */

  private readonly appliedTier = computed(() => {
    const brief = this.brief();
    const creator = this.creator();
    const channel = this.primaryChannel();
    if (!brief || !creator || channel == null) {
      return null;
    }
    return this.pricingModelService.getAppliedAudienceTier(
      channel as SocialChannelEnum,
      brief,
      creator,
      this.activeOverlay(),
    );
  });

  /** e.g. `Established (100K+)` — the band the creator's rate came from. */
  protected readonly tierLabel = computed(() => {
    const tier = this.appliedTier();
    if (!tier?.title) {
      return null;
    }
    const from = this.formatService.convertIntToString(tier.followersFrom);
    const to = this.formatService.convertIntToString(tier.followersTo);
    return `${tier.title} (${from}–${to})`;
  });

  protected readonly description = computed(() =>
    headerDescription({
      creatorFirstName: this.creator()?.firstName,
      primarySocialChannel: this.primaryChannel(),
      putForwardCount: this.putForwardChannels().length,
      tierTitle: this.tierLabel(),
    }),
  );

  protected readonly creatorInitials = computed(() =>
    personInitials(this.creator()?.firstName, this.creator()?.lastName),
  );

  protected readonly creatorName = computed(() => {
    const creator = this.creator();
    if (!creator) {
      return '';
    }
    const lastInitial = creator.lastName ? `${creator.lastName.charAt(0)}` : '';
    return `${creator.firstName ?? ''} ${lastInitial}`.trim();
  });

  /* ── Classes ────────────────────────────────────────────────────────────── */

  protected rowClassFor(row: NetworkRow): string {
    return rowClass({ included: row.included, interactive: !row.required });
  }

  protected checkboxClassFor(row: NetworkRow): string {
    return checkboxClass({ included: row.included, disabled: row.required });
  }

  protected readonly breakdownRowClass = breakdownRowClass;
  protected readonly breakdownLabelClass = breakdownLabelClass;
  protected readonly breakdownFigureClass = breakdownFigureClass;

  /* ── Commit and dismiss ─────────────────────────────────────────────────── */

  /**
   * Whether this creator's hire is in flight.
   *
   * Read from the service's own `actionsProcessing` list, which is cleared on both
   * success and failure — a local flag reset on a timer (as the drawer does) either
   * unlocks the button before the request lands or leaves it stuck after a failure.
   */
  protected readonly isHiring = computed(() => {
    const applicationId = this.application()?.id;
    return !!applicationId && this.manageBriefTabsService.actionsProcessing().includes(applicationId);
  });

  /**
   * Hand the hire to whichever page opened us.
   *
   * The commit path is deliberately untouched: the host's `openItemisedHiring()` sees a
   * second HIRE action while the modal is open and calls `hireCreator()`, which reads
   * the same draft this modal has been editing.
   */
  protected hire(): void {
    const application = this.application();
    if (!application || this.hireBlocked()) {
      return;
    }
    this.centralManagement.portfolioAction.set({
      applicant: application,
      actionType: ActionTypesEnum.HIRE,
    });
  }

  /** Cancel, ×, Esc and the backdrop all land here — see `snapshots`. */
  protected cancel(): void {
    const applicationId = this.application()?.id;
    if (applicationId) {
      const snapshot = this.snapshots.get(applicationId);
      if (snapshot) {
        this.centralManagement.crossPlatformHireDraft.update((drafts) => ({
          ...drafts,
          [applicationId]: [...snapshot],
        }));
      }
    }
    this.close.emit();
  }

  /** A click that lands on the backdrop itself, not on the panel inside it. */
  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.cancel();
    }
  }

  /**
   * Open the creator's portfolio over the top.
   *
   * Flagged to reopen on the way back, exactly as the drawer does — the profile is a
   * separate overlay and closing it should return the brand to the decision they were
   * halfway through, not to the table.
   */
  protected viewCreatorProfile(): void {
    const creator = this.creator();
    if (!creator) {
      return;
    }
    const application = this.application();
    const applicants = this.manageBriefTabsService.filteredApplicants() ?? [];
    const navItems =
      applicants.length > 0
        ? applicants
            .filter((item) => !!item.creator)
            .map((item) => ({ creator: item.creator!, application: item }))
        : application
          ? [{ creator, application }]
          : [{ creator }];

    this.magicSearchService.openCreatorPortfolio(creator, { application, navItems });
    this.centralManagement.reopenItemisedHireAfterProfileClose.set(true);
    this.close.emit();
  }
}
